'use client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import Svg from '../Svg';
import { MagnifyingGlassIcon } from '@/icons';
import { useHeaderContext } from '@/context/headerContext';
import { debounce } from '@/utils/helper';
import { useCleverTap } from '@/context/clevertapContext';
import { AnimatePresence, motion } from 'framer-motion';
import { usePathname } from 'next/navigation';

interface SearchBarPropTypes {
  headerClass: string;
}
const placeholders = [
  '“Plum Cake”',
  '“Strawberries” ',
  '"Gift Hampers"',
  '"Cheese Platters"',
  '"Chocolates"',
  '"Sourdough Bread"',
  '"Cheese"',
];

const SearchBar: React.FC<SearchBarPropTypes> = ({ headerClass }) => {
  const {
    query = '',
    searchBarRef,
    isSearchDrawerOpen,
    setQuery,
    toggleIsSearchDrawerOpen,
    deepSearch,
    fetchAutoCompleteRequest,
    setShowNoProductsFound,
  } = useHeaderContext();
  const { pushEvent } = useCleverTap();
  const prevScrollY = useRef(0);
  const pathname = usePathname();

  const [isSearchBarHidden, setIsSearchBarHidden] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  useEffect(() => {
    if (pathname === '/') {
      setQuery('');
    }
  }, [pathname, setQuery]);

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex(prevIndex => (prevIndex + 1) % placeholders.length);
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  const isHeaderOpaque = useCallback((keywordString: string) => {
    return keywordString.includes('bg-white');
  }, []);

  const debouncedAutoCompleteSearch = useMemo(() => {
    return debounce((value: string) => {
      fetchAutoCompleteRequest(value);
    }, 600);
  }, [fetchAutoCompleteRequest]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (
        Math.abs(currentScrollY - prevScrollY.current) > 8.5 * 16 &&
        !isSearchDrawerOpen
      ) {
        // If the scroll difference is greater than 8.5rem (8.5 * 16px),
        // toggle the visibility of the search bar
        setIsSearchBarHidden(currentScrollY > prevScrollY.current);
        prevScrollY.current = currentScrollY;
      }
    };
    if (typeof window === 'object') {
      window.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (typeof window === 'object') {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isSearchDrawerOpen]);

  useEffect(() => {
    if (isSearchDrawerOpen && isSearchDrawerOpen) {
      setIsSearchBarHidden(false);
    }
  }, [isSearchDrawerOpen, isSearchDrawerOpen]);

  return (
    <div
      className={twMerge(
        'relative max-h-full w-full transform-gpu transition-all duration-300 ease-in-out max-lg:pb-4 max-md:max-h-full max-md:overflow-hidden lg:mx-4 lg:max-w-129',
        isSearchBarHidden &&
          'max-md:z-[-5] max-md:-mb-44 max-md:mt-[0rem] max-md:max-h-[0%] max-md:-translate-y-full max-md:opacity-0'
      )}>
      <div className='relative'>
        <input
          id='searchBar'
          type='search'
          spellCheck='false'
          role='search'
          autoComplete='off'
          aria-label='Search Bar'
          placeholder={isSearchDrawerOpen ? 'Search' : ''}
          onChange={e => {
            setShowNoProductsFound(null);
            setQuery(e?.target.value);
            debouncedAutoCompleteSearch(e.target.value);
          }}
          onKeyDown={(e: any) => {
            if (e.key === 'Enter') {
              setTimeout(
                () => document.getElementById('searchBar')?.blur(),
                300
              );
              deepSearch(e);
            }
          }}
          value={(query && query) || ''}
          className={twMerge(
            'search-input w-full rounded-[3.75rem] px-7.5 py-3 pl-10 outline-hidden backdrop-blur-xs placeholder:select-none placeholder:truncate placeholder:text-sm placeholder:font-semibold placeholder:opacity-70 max-[425px]:placeholder:text-xs placeholder:invisible',
            isHeaderOpaque(headerClass)
              ? 'bg-black/5 placeholder:text-black'
              : 'bg-black/40 placeholder:text-white',
            isSearchDrawerOpen && 'placeholder:visible'
          )}
          onFocusCapture={() => {
            if (!isSearchDrawerOpen) {
              toggleIsSearchDrawerOpen(true);
              pushEvent({
                event: 'Searchbar Clicked',
                data: {
                  'Page URL': window.location.href,
                  'Page Name': document.title,
                },
              });
            }
          }}
          ref={searchBarRef}
        />
        <div className='absolute right-2 top-1/2 -translate-y-1/2'>
          {query && (
            <button
              onClick={() => {
                setQuery('');
                searchBarRef.current?.focus();
              }}
              aria-label='Clear search'
              title='Clear search'
              className='group flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-300 transition-colors'>
              <Svg
                className='w-4 h-4 stroke-current text-gray-600 group-hover:text-fig'
                viewBox='0 0 16 16'>
                <path
                  d='M4 4l8 8M12 4l-8 8'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </Svg>
            </button>
          )}
        </div>
        {!query && (
          <div
            className={twMerge(
              'absolute pl-1 left-10 right-4 top-6 transform -translate-y-1/2 overflow-hidden h-full opacity-70 text-sm font-semibold max-[425px]:text-xs z-20 pointer-events-none',
              isHeaderOpaque(headerClass) ? 'text-black' : 'text-white'
            )}>
            {!isSearchDrawerOpen && (
              <span className='flex absolute md:top-[14px] top-[18px] select-none gap-x-1'>
                Search
                <AnimatePresence mode='wait'>
                  <motion.div
                    key={placeholderIndex}
                    initial={{ y: '100%', opacity: 0 }}
                    animate={{ y: '0%', opacity: 1 }}
                    exit={{ y: '-100%', opacity: 0 }}
                    transition={{ duration: 0.5 }}>
                    {placeholders[placeholderIndex]}
                  </motion.div>
                </AnimatePresence>
              </span>
            )}
          </div>
        )}
        <Svg
          className={twMerge(
            'absolute left-3 top-3 h-6 w-6 lg:top-[0.65rem]',
            isHeaderOpaque(headerClass) ? 'stroke-black' : 'stroke-white'
          )}>
          <MagnifyingGlassIcon />
        </Svg>
      </div>
    </div>
  );
};

export default SearchBar;
